import * as React from 'react'
import Helmet from 'react-helmet'

import { SiteMetadataMarketing } from '../hooks/useSiteMetadata'
import { ErrorBoundary } from './ErrorBoundary'

// eslint-disable-next-line
declare var window: any

interface ThirdPartyProps {
  marketingMetaData: SiteMetadataMarketing
  anonymousId?: string
  withMarketing?: boolean
}

let loaded = false
const ThirdPartyComponent: React.FC<ThirdPartyProps> = ({
  marketingMetaData,
  anonymousId = '',
  withMarketing = true,
}) => {
  React.useEffect(() => {
    // Avoid loading scripts twice on the same page to avoid unexpexted side effects
    if (loaded || !withMarketing) {
      return
    }

    const loadScripts = () => {
      // See backupTimeoutForScriptLoading above
      loaded = true
      loadThirdPartyScripts(marketingMetaData)
    }

    if (document.readyState === 'loading') {
      // Loading hasn't finished yet
      document.addEventListener('DOMContentLoaded', loadScripts)
    } else {
      // `DOMContentLoaded` has already fired
      loadScripts()
    }

    // Clean up the timeout if the load event fires
    return () => {
      document.removeEventListener('DOMContentLoaded', loadScripts)
    }
  }, [])

  const loadThirdPartyScripts = (data: SiteMetadataMarketing) => {
    // data_iub_purposes = 2 (functionality)
    if (data.intercomAppId) {
      window.intercomSettings = {
        app_id: data.intercomAppId,
      }
      const script = document.createElement('script')
      script.type = 'text/plain'
      script.setAttribute('class', '_iub_cs_activate-inline')
      script.setAttribute('data-iub-purposes', '2')
      script.innerText = `
      (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + ${JSON.stringify(
        marketingMetaData.intercomAppId
      )};var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
    `
      document.body.appendChild(script)
    }

    // data_iub_purposes = 5 (marketing)
    if (window.uetq) {
      const script = document.createElement('script')
      script.type = 'text/plain'
      script.setAttribute('class', '_iub_cs_activate-inline')
      script.setAttribute('data-iub-purposes', '5')
      script.innerText = `
      window.uetq = window.uetq || [];
      window.uetq.push('consent', 'update', {
      'ad_storage': 'granted'
      });
    `
      document.body.appendChild(script)
    }

    if (data.firstPromoterId && data.firstPromoterDomain) {
      const script = document.createElement('script')
      script.type = 'text/plain'
      script.defer = true
      script.setAttribute('class', '_iub_cs_activate-inline')
      script.setAttribute('data-iub-purposes', '5')
      script.innerText = `
      (function(){var t=document.createElement("script");t.type="text/javascript",t.async=!0,t.src='https://cdn.firstpromoter.com/fprom.js',t.onload=t.onreadystatechange=function(){var t=this.readyState;if(!t||"complete"==t||"loaded"==t)try{$FPROM.init("${data.firstPromoterId}","${data.firstPromoterDomain}")}catch(t){}};var e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(t,e)})();
    `
      document.body.appendChild(script)
    }

    if (data.profitWellRetainAuth) {
      const script = document.createElement('script')
      script.id = 'profitwell-js'
      script.dataset.pwAuth = data.profitWellRetainAuth
      script.type = 'text/plain'
      script.setAttribute('class', '_iub_cs_activate-inline')
      script.setAttribute('data-iub-purposes', '5')
      script.innerText = `
      (function(i,s,o,g,r,a,m){i[o]=i[o]||function(){(i[o].q=i[o].q||[]).push(arguments)}; a=s.createElement(g);m=s.getElementsByTagName(g)[0];a.async=1;a.src=r+'?auth='+s.getElementById(o+'-js').getAttribute('data-pw-auth');m.parentNode.insertBefore(a,m);})(window,document,'profitwell','script','https://public.profitwell.com/js/profitwell.js');
      profitwell('start', {});
    `
      document.body.appendChild(script)
    }

    if (data.segmentId) {
      const script = document.createElement('script')
      script.type = 'text/plain'
      script.setAttribute('class', '_iub_cs_activate-inline')
      script.setAttribute('data-iub-purposes', '5')
      script.innerText = `
        !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on"];analytics.factory=function(t){return function(){var e=Array.prototype.slice.call(arguments);e.unshift(t);analytics.push(e);return analytics}};for(var t=0;t<analytics.methods.length;t++){var e=analytics.methods[t];analytics[e]=analytics.factory(e)}analytics.load=function(t,e){var n=document.createElement("script");n.type="text/javascript";n.async=!0;n.src="https://cdn.segment.com/analytics.js/v1/"+t+"/analytics.min.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(n,a);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.4.0";
        ${`analytics.load("${data.segmentId}", { integrations: { All: true } });`}
        analytics.page({}, { anonymousId: "${anonymousId}" });
        }}();
      `
      document.body.appendChild(script)
    }

    if (data.navatticId) {
      const script = document.createElement('script')
      script.id = 'navattic-js'
      script.type = 'text/plain'
      script.setAttribute('class', '_iub_cs_activate-inline')
      script.setAttribute('data-iub-purposes', '5')
      script.innerText = `
    void 0 === window.navatticQueue &&
      (window.navatticQueue = [],
       window.navattic = new Proxy({}, {
         get: function(n, t, e) {
           return function() {
             for (var n = arguments.length, t = Array(n), e = 0; e < n; e++) t[e] = arguments[e];
             return navatticQueue.push({ function: t, arguments: t });
           }
         }
       }));

    (function(d, s, id, key) {
      var js = d.createElement(s);
      js.async = true;
      js.src = 'https://js.navattic.com/sdk.js';
      js.setAttribute('data-key', key);
      var m = d.getElementsByTagName(s)[0];
      m.parentNode.insertBefore(js, m);
    })(document, 'script', 'navattic-sdk', "${data.navatticId}");
  `
      document.body.appendChild(script)
    }
  }

  // Iubenda cookies GDPR stuff
  // https://www.iubenda.com/en/help/1205-how-to-configure-your-cookie-solution-advanced-guide#api
  // IMPORTANT NOTE: if you are copy pasting this snippet from the iubenda web take a look to not override the callbacks
  React.useEffect(() => {
    window.uetq = window.uetq || []
    window.uetq.push('consent', 'default', {
      ad_storage: 'denied',
    })

    // no cookie popup on privacy page (https://airfocus.com/privacy/)
    // visitor has to be able to see the privacy policy without accepting cookies
    if (window.location.pathname.includes('privacy')) {
      return
    }

    /* eslint-disable */
    if (!window._iub) {
      window._iub = {
        csConfiguration: {
          gdprAppliesGlobally: false,
          lgpdAppliesGlobally: false,
          askConsentAtCookiePolicyUpdate: true,
          countryDetection: true,
          cookiePolicyInOtherWindow: true,
          enableFadp: true,
          enableLgpd: true,
          enableUspr: true,
          perPurposeConsent: true,
          purposes: '1, 2, 4, 5',
          lang: 'en',
          siteId: 1167550,
          whitelabel: false,
          cookiePolicyId: 91277624,
          cookiePolicyUrl: 'https://airfocus.com/privacy/',
          i18n: {
            en: {
              banner: {
                dynamic: {
                  body: '<div id="iubenda-cs-title">Here, have a cookie &#x1f36a;</div><div id="iubenda-cs-paragraph">We and selected partners use cookies or similar technologies as specified in the <a href="https://airfocus.com/privacy/" class="iubenda-cs-cookie-policy-lnk" target="_blank" rel="noopener">cookie policy</a> to track the performance of the website, and to provide a better user experience. We don’t store any personal details. You can consent to the use of such technologies by closing this notice.</div>',
                },
              },
            },
          },
          banner: {
            customizeButtonDisplay: true,
            acceptButtonColor: '#33b6ff',
            acceptButtonDisplay: true,
            backgroundOverlay: true,
            closeButtonDisplay: false,
            explicitWithdrawal: true,
            position: 'float-center',
            showTitle: false,
          },
        },
      }

      document.head.appendChild(
        Object.assign(document.createElement('script'), {
          type: 'text/javascript',
          src: '//cs.iubenda.com/sync/1167550.js',
        })
      )
      document.head.appendChild(
        Object.assign(document.createElement('script'), {
          type: 'text/javascript',
          src: '//cdn.iubenda.com/cs/gpp/stub.js',
        })
      )
      document.head.appendChild(
        Object.assign(document.createElement('script'), {
          type: 'text/javascript',
          src: '//cdn.iubenda.com/cs/iubenda_cs.js',
          async: true,
        })
      )
    }
  }, [])

  return (
    <Helmet>
      <link rel="preconnect" href="https://cdn.iubenda.com" crossOrigin="" />
      <link rel="preconnect" href="https://hits-i.iubenda.com" crossOrigin="" />
      <script async type="application/ld+json">
        {`
            {
              "@context": "https://schema.org/",
              "@type": "Organization",
              "@id": "#Organization",
              "url": "https://airfocus.com/",
              "name": "airfocus",
              "description": "Home for products and the people who build them.",
              "image": "https://i.imgur.com/RFXZ7wv.png",
              "logo": "https://i.imgur.com/RFXZ7wv.png",
              "sameAs": [
                "https://www.facebook.com/airfocus/",
                "https://www.youtube.com/channel/UCRFwwMHydrKAY5ky2wdX6SA",
                "https://www.instagram.com/airfocusapp/",
                "https://www.linkedin.com/company/airfocus",
                "https://twitter.com/airfocus"
              ]
            }
          `}
      </script>
    </Helmet>
  )
}

export const ThirdParty: typeof ThirdPartyComponent = (props) => {
  return (
    <ErrorBoundary>
      <ThirdPartyComponent {...props} />
    </ErrorBoundary>
  )
}
